import { STORAGE_KEY, ENGINE_URL_KEY } from './portalSessionKeys';

const POST_LOGIN_REDIRECT_KEY = 'phase2_post_login_redirect_v1';

function readSession() {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function decodeJwtPayload(token) {
  try {
    const parts = String(token || '').split('.');
    if (parts.length !== 3) return null;
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

/** True when saved portal session includes a JWT / token string that is not past exp. */
export function hasValidPortalSession() {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    const s = readSession();
    const token = typeof s?.token === 'string' ? s.token.trim() : '';
    if (!token) {
      return false;
    }
    const payload = decodeJwtPayload(token);
    if (payload && typeof payload.exp === 'number') {
      // 30s skew
      if (Date.now() / 1000 >= payload.exp - 30) {
        return false;
      }
    }
    return true;
  } catch {
    return false;
  }
}

export function clearPortalSession() {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Confirm the saved JWT still works against the engine that issued it.
 * On failure clears local session so the user is sent back through login.
 */
export async function validatePortalSessionAgainstEngine() {
  if (typeof window === 'undefined') {
    return { ok: true };
  }
  if (!hasValidPortalSession()) {
    clearPortalSession();
    return { ok: false, reason: 'missing_or_expired' };
  }
  const session = readSession();
  const token = (session?.token || '').trim();
  const engineUrl = (
    (session?.engineUrl || '').trim() ||
    (window.localStorage.getItem(ENGINE_URL_KEY) || '').trim() ||
    (process.env.REACT_APP_ENGINE_URL || '').trim()
  ).replace(/\/$/, '');
  if (!engineUrl || !token) {
    clearPortalSession();
    return { ok: false, reason: 'missing_engine' };
  }
  try {
    const res = await fetch(`${engineUrl}/v1/account/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      return { ok: true };
    }
    let reason = `http_${res.status}`;
    try {
      const body = await res.json();
      reason = body.reason || body.message || reason;
    } catch {
      /* ignore */
    }
    clearPortalSession();
    return { ok: false, reason };
  } catch {
    // Network blip — do not wipe session; engine may be waking on Render free tier.
    return { ok: true, degraded: true };
  }
}

/** Remember where the user was going before we sent them to sign-in (SPA paths only). */
export function stashRedirectBeforeLogin() {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const path = window.location.pathname + window.location.search + window.location.hash;
    if (
      path.startsWith('/auth/') ||
      path === '/login' ||
      path === '/signup' ||
      path === '/auth'
    ) {
      return;
    }
    window.sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, path);
  } catch {
    /* ignore quota / private mode */
  }
}

/** Path to open after successful login (default `/`). Clears stored value. */
export function consumePostLoginRedirect(fallback = '/') {
  if (typeof window === 'undefined') {
    return fallback;
  }
  try {
    const v = window.sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    window.sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    if (v && v.startsWith('/') && !v.startsWith('//')) {
      return v;
    }
  } catch {
    /* ignore */
  }
  return fallback;
}
